import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Box, Typography, Avatar, CircularProgress, Divider } from '@mui/material';

function formatKickoff(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleString('en-US', { weekday: 'short', hour: 'numeric', minute: '2-digit' });
}

function NFLScheduleSidebar() {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios
      .get('https://site.api.espn.com/apis/site/v2/sports/football/nfl/scoreboard')
      .then((res) => {
        setGames(res.data?.events || []);
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, []);

  if (loading)
    return (
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
        <CircularProgress size={22} color="primary" />
        <Typography variant="caption" sx={{ color: '#555' }}>Checking scoreboard…</Typography>
      </Box>
    );

  if (error)
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="caption" sx={{ color: '#777' }}>
          Scoreboard is offline (offseason). Check back closer to kickoff.
        </Typography>
      </Box>
    );

  if (!games.length)
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="caption" sx={{ color: '#777' }}>
          No scheduled games this week. Offseason mode.
        </Typography>
      </Box>
    );

  return (
    <Box sx={{ p: 1.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 800, fontSize: '0.95rem', mb: 1.5 }}>
        This Week&apos;s Schedule
      </Typography>
      {games.map((event, idx) => {
        const comp = event.competitions?.[0];
        const teams = comp?.competitors || [];
        const home = teams.find((t) => t.homeAway === 'home');
        const away = teams.find((t) => t.homeAway === 'away');
        const status = comp?.status?.type?.description || comp?.status?.type?.state;
        const completed = comp?.status?.type?.completed;

        return (
          <Box key={event.id || idx}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, py: 0.8 }}>
              {[away, home].map((team, i) => (
                <Box key={team?.id || i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  {team?.team?.logo && (
                    <Avatar src={team.team.logo} alt={team.team.abbreviation} sx={{ width: 20, height: 20, borderRadius: '3px' }} />
                  )}
                  <Typography variant="caption" sx={{ flex: 1, color: '#c0d0e0', fontSize: '0.74rem', fontWeight: 600 }}>
                    {team?.team?.abbreviation}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#888', fontSize: '0.7rem' }}>
                    {completed ? (team?.score?.displayValue ?? team?.score) : ''}
                  </Typography>
                </Box>
              ))}
              <Typography variant="caption" sx={{ color: '#555', fontSize: '0.66rem' }}>
                {completed ? status : formatKickoff(event.date)}
              </Typography>
            </Box>
            {idx < games.length - 1 && <Divider sx={{ borderColor: '#132032' }} />}
          </Box>
        );
      })}
    </Box>
  );
}

export default NFLScheduleSidebar;
